/**
 * PersonDetail — the answer to "what is this person working on?"
 *
 * Replaces the modal that used to pop up off Team cards / Role cards
 * with a real page that's reachable from anywhere a person's name
 * appears. The same data the modal showed lives here, plus a
 * 12-week utilization strip.
 *
 * Route: /people/:name (URL-decoded by react-router)
 *
 * Data sources:
 *   - /capacity/person-assignments?person_name=...
 *     (assignments table: projects + hours)
 *   - /capacity/person-heatmap
 *     (12-week utilization + role + team + capacity)
 *
 * Both endpoints already exist and power other surfaces, so this is a
 * pure presentation layer.
 */
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  TrendingUp,
  User as UserIcon,
} from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { cn } from "@/lib/cn";
import api from "@/lib/api";
import { useRoster } from "@/hooks/useRoster";
import { statusLabel, statusPillClass } from "@/lib/projectStatus";

// ---------------------------------------------------------------------------
// Types — mirror the existing endpoint shape used by the (soon-retired)
// PersonAssignmentsModal. Kept inline so this page has no hidden coupling
// to that modal's file.
// ---------------------------------------------------------------------------

interface PersonAssignmentItem {
  entity_kind: "project";
  id: string;
  name: string;
  role_key: string;
  status: string | null;
  hours_per_week: number;
}

interface PersonAssignments {
  person_name: string;
  capacity_hrs_week: number;
  total_demand_hrs_week: number;
  utilization_pct: number;
  items: PersonAssignmentItem[];
}

const ROLE_LABEL: Record<string, string> = {
  pm: "Project Manager",
  ba: "Business Analyst",
  functional: "Functional",
  technical: "Technical",
  developer: "Developer",
  infrastructure: "Infrastructure",
  dba: "DBA",
  erp: "ERP Consultant",
  operations: "Operations",
};

function utilBadgeClass(util: number): string {
  if (util >= 1.0) return "bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300 ring-red-200 dark:ring-red-500/30";
  if (util >= 0.8) return "bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 ring-amber-200 dark:ring-amber-500/30";
  if (util > 0) return "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30";
  return "bg-raised text-ink-3 ring-line";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PersonDetail() {
  const { name: nameParam } = useParams<{ name: string }>();
  const personName = nameParam ? decodeURIComponent(nameParam) : "";

  const assignmentsQ = useQuery<PersonAssignments>({
    queryKey: ["capacity", "person-assignments", personName],
    queryFn: () =>
      api
        .get("/capacity/person-assignments", { params: { person_name: personName } })
        .then((r) => r.data),
    enabled: !!personName,
  });

  // Role/team/capacity for the hero come straight from the roster —
  // the 12-week heatmap strip was retired 2026-09-11 with the
  // flat-hrs/week capacity model.
  const rosterQ = useRoster();
  const member = (Array.isArray(rosterQ.data) ? rosterQ.data : []).find(
    (m) => m.name === personName,
  );

  const data = assignmentsQ.data;
  const projects = data?.items.filter((i) => i.entity_kind === "project") ?? [];

  const util = data?.utilization_pct ?? 0;
  const capacity = data?.capacity_hrs_week ?? member?.project_capacity_hrs ?? 0;
  const demand = data?.total_demand_hrs_week ?? 0;

  return (
    <>
      <TopBar
        title={personName || "Person"}
        subtitle="Everything this person is on — projects and load."
      >
        <Link
          to="/roster"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-ink-3 hover:bg-raised"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Roster
        </Link>
      </TopBar>

      <div className="space-y-6 p-8">
        {/* Hero card — role/team/capacity/util + 12-week strip */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="rounded-xl bg-surface p-6 ring-1 ring-line"
        >
          <div className="flex flex-wrap items-start gap-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-navy-100 dark:bg-brand-navy-500/15 text-brand-navy-700 dark:text-brand-navy-300 ring-1 ring-brand-navy-200 dark:ring-brand-navy-500/30">
                <UserIcon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xl font-semibold tracking-tight text-ink">
                  {personName}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-ink-3">
                  {member && (
                    <span className="inline-flex items-center gap-1">
                      <Briefcase className="h-3 w-3 text-ink-subtle" />
                      {ROLE_LABEL[member.role_key] ?? member.role}
                    </span>
                  )}
                  {member?.team && (
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="h-3 w-3 text-ink-subtle" />
                      {member.team}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="ml-auto flex items-center gap-6">
              <Stat label="Capacity" value={`${capacity}h/wk`} />
              <Stat label="Demand" value={`${demand}h/wk`} />
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-ink-subtle">
                  Utilization
                </div>
                <div className="mt-0.5">
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-sm font-semibold ring-1 tabular-nums",
                      utilBadgeClass(util),
                    )}
                  >
                    {Math.round(util * 100)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

        </motion.div>

        {/* Loading / error gates */}
        {(assignmentsQ.isLoading || rosterQ.isLoading) && (
          <div className="rounded-xl bg-surface p-8 text-center text-sm text-ink-muted ring-1 ring-line">
            Loading {personName}'s assignments…
          </div>
        )}
        {assignmentsQ.isError && (
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
            Couldn't load assignments. {(assignmentsQ.error as Error)?.message}
          </div>
        )}

        {/* Projects */}
        {data && (
          <AssignmentTable
            icon={<TrendingUp className="h-4 w-4 text-brand-navy-500" />}
            title="Projects"
            empty="Not assigned to any active projects."
            items={projects}
          />
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Hero stat — same shape used elsewhere in the app for label/value pairs
// in a card header. Local because the global pattern hasn't been factored.
// ---------------------------------------------------------------------------
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-wider text-ink-subtle">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums text-ink">
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One assignment table — projects.
// ---------------------------------------------------------------------------
function AssignmentTable({
  icon,
  title,
  empty,
  items,
}: {
  icon: React.ReactNode;
  title: string;
  empty: string;
  items: PersonAssignmentItem[];
}) {
  return (
    <div className="overflow-hidden rounded-xl bg-surface ring-1 ring-line">
      <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink">
          {icon}
          {title}
          <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-bold tabular-nums text-ink-3">
            {items.length}
          </span>
        </div>
        {items.length > 0 && (
          <div className="text-[11px] tabular-nums text-ink-muted">
            Total:{" "}
            <span className="font-semibold text-ink-2">
              {items.reduce((s, i) => s + i.hours_per_week, 0)}h/wk
            </span>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="px-5 py-8 text-center text-xs text-ink-subtle">{empty}</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-soft bg-canvas text-left text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
              <th className="px-5 py-2">ID</th>
              <th className="px-5 py-2">Name</th>
              <th className="px-5 py-2">Role</th>
              <th className="px-5 py-2">Status</th>
              <th className="px-5 py-2 text-right">Hours/wk</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const href = `/portfolio/${it.id}`;
              return (
                <tr
                  key={`${it.entity_kind}-${it.id}-${it.role_key}`}
                  className="border-b border-line-soft transition-colors hover:bg-raised"
                >
                  <td className="px-5 py-2 align-middle">
                    <Link
                      to={href}
                      className="inline-block rounded-md bg-raised px-1.5 py-0.5 font-mono text-[10px] font-bold tabular-nums text-ink-3"
                    >
                      {it.id}
                    </Link>
                  </td>
                  <td className="px-5 py-2 align-middle">
                    <Link
                      to={href}
                      className="font-medium text-ink hover:text-brand-navy-700 dark:hover:text-brand-navy-300 hover:underline underline-offset-2 decoration-brand-navy-400"
                    >
                      {it.name}
                    </Link>
                  </td>
                  <td className="px-5 py-2 align-middle text-xs text-ink-3">
                    {ROLE_LABEL[it.role_key] ?? it.role_key}
                  </td>
                  <td className="px-5 py-2 align-middle">
                    {it.status ? (
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold",
                          statusPillClass(it.status),
                        )}
                      >
                        {statusLabel(it.status)}
                      </span>
                    ) : (
                      <span className="text-xs text-ink-subtle">—</span>
                    )}
                  </td>
                  <td className="px-5 py-2 align-middle text-right text-sm font-semibold tabular-nums text-ink">
                    {it.hours_per_week}h
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
